import React, { useState } from 'react';
import { updateTeamRoundData } from '../../firebase/db';
import { ATTRIBUTES, SEGMENT_NAMES } from '../../scoring';

const styles = {
    container: { padding: '16px 20px' },
    roundSection: {
        marginBottom: '16px', padding: '12px', backgroundColor: '#fff',
        borderRadius: '6px', border: '1px solid #e0d8f0',
    },
    roundTitle: {
        fontSize: '14px', fontWeight: 'bold', color: '#4b2e83',
        marginBottom: '10px', borderBottom: '1px solid #e0d8f0', paddingBottom: '4px',
    },
    row: {
        display: 'flex', alignItems: 'flex-start', marginBottom: '6px', fontSize: '13px',
    },
    label: {
        width: '70px', fontWeight: 'bold', color: '#666', flexShrink: 0,
    },
    value: {
        flex: 1, color: '#333',
    },
    editBtn: {
        marginLeft: '8px', padding: '2px 8px', fontSize: '11px',
        backgroundColor: 'transparent', color: '#1976d2', border: '1px solid #1976d2',
        borderRadius: '3px', cursor: 'pointer',
    },
    saveBtn: {
        padding: '4px 12px', fontSize: '12px', backgroundColor: '#4b2e83', color: '#fff',
        border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold',
    },
    cancelBtn: {
        padding: '4px 12px', fontSize: '12px', backgroundColor: '#ccc', color: '#333',
        border: 'none', borderRadius: '4px', cursor: 'pointer', marginLeft: '6px',
    },
    attrInput: {
        width: '40px', padding: '3px 4px', fontSize: '12px', textAlign: 'center',
        border: '1px solid #ccc', borderRadius: '3px', marginRight: '4px',
    },
    dollarInput: {
        width: '70px', padding: '3px 4px', fontSize: '12px', textAlign: 'right',
        border: '1px solid #ccc', borderRadius: '3px', marginRight: '4px',
    },
    priceInput: {
        width: '80px', padding: '3px 6px', fontSize: '12px',
        border: '1px solid #ccc', borderRadius: '3px',
    },
    noData: {
        fontSize: '13px', color: '#999', fontStyle: 'italic',
    },
    segBadge: {
        display: 'inline-block', padding: '2px 6px', fontSize: '11px',
        backgroundColor: '#e8eaf6', color: '#283593', borderRadius: '10px',
        marginRight: '4px', marginBottom: '2px',
    },
    editSection: {
        marginTop: '4px', marginBottom: '6px',
    },
    editLabel: {
        fontSize: '11px', color: '#666', marginBottom: '2px',
    },
    checkboxLabel: {
        display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px',
        marginRight: '12px', marginBottom: '4px', cursor: 'pointer',
    },
};

const AD_KEYS = [...ATTRIBUTES, 'Broad'];

const fmtDollar = (val) => {
    const n = parseFloat(val) || 0;
    return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const fmtAdSpend = (val) => {
    const n = parseFloat(val) || 0;
    if (n === 0) return '$0';
    if (n >= 1000) return `$${(n / 1000).toFixed(0)}K`;
    return `$${n.toFixed(0)}`;
};

const TeamDataPanel = ({ teamName, teamData, onUpdate, showToast }) => {
    const [editingField, setEditingField] = useState(null);
    const [attrInputs, setAttrInputs] = useState({});
    const [priceInput, setPriceInput] = useState('');
    const [rndInputs, setRndInputs] = useState({});
    const [perfAdsInputs, setPerfAdsInputs] = useState({});
    const [brandAdsInputs, setBrandAdsInputs] = useState({});
    const [segmentInputs, setSegmentInputs] = useState([]);

    const rounds = teamData.rounds || [];

    // --- Start edit handlers ---

    const startEditBike = (roundIndex) => {
        const round = rounds[roundIndex];
        const bike = round?.manufacturing?.upgraded_bike || round?.manufacturing?.attributes || {};
        const inputs = {};
        ATTRIBUTES.forEach(attr => { inputs[attr] = bike[attr] ?? 1; });
        setAttrInputs(inputs);
        setEditingField(`${roundIndex}-bike`);
    };

    const startEditPrice = (roundIndex) => {
        const round = rounds[roundIndex];
        const price = round?.profit_margin?.price_per_bike_dollars || '0';
        setPriceInput(String(parseFloat(price) || 0));
        setEditingField(`${roundIndex}-price`);
    };

    const startEditRnd = (roundIndex) => {
        const round = rounds[roundIndex];
        const upgrades = round?.rnd?.upgrades || {};
        const inputs = {};
        ATTRIBUTES.forEach(attr => { inputs[attr] = parseFloat(upgrades[attr]) || 0; });
        setRndInputs(inputs);
        setEditingField(`${roundIndex}-rnd`);
    };

    const startEditAds = (roundIndex) => {
        const round = rounds[roundIndex];
        const perf = round?.advertising?.performance_ads || {};
        const brand = round?.advertising?.brand_ads || {};
        const perfInputs = {};
        const brandInputs = {};
        AD_KEYS.forEach(key => {
            perfInputs[key] = parseFloat(perf[key]) || 0;
            brandInputs[key] = parseFloat(brand[key]) || 0;
        });
        setPerfAdsInputs(perfInputs);
        setBrandAdsInputs(brandInputs);
        setEditingField(`${roundIndex}-ads`);
    };

    const startEditSegments = (roundIndex) => {
        const round = rounds[roundIndex];
        setSegmentInputs([...(round?.advertising?.segments || [])]);
        setEditingField(`${roundIndex}-segments`);
    };

    // --- Save handlers ---

    const handleSaveBike = async (roundIndex) => {
        const clamped = {};
        ATTRIBUTES.forEach(attr => {
            const v = parseInt(attrInputs[attr], 10);
            clamped[attr] = Math.min(5, Math.max(1, isNaN(v) ? 1 : v));
        });
        try {
            await updateTeamRoundData(teamName, 'Master', roundIndex, {
                manufacturing: {
                    ...rounds[roundIndex].manufacturing,
                    attributes: clamped,
                    upgraded_bike: clamped,
                    total_cost: Object.values(clamped).reduce((s, v) => s + v, 0),
                },
            });
            showToast(`Bike attributes updated for Round ${roundIndex + 1}.`, 'success');
            setEditingField(null);
            onUpdate();
        } catch (err) {
            showToast('Error saving bike attributes.', 'error');
        }
    };

    const handleSavePrice = async (roundIndex) => {
        const price = parseFloat(priceInput);
        if (isNaN(price) || price <= 0) {
            showToast('Price must be a positive number.', 'error');
            return;
        }
        try {
            await updateTeamRoundData(teamName, 'Master', roundIndex, {
                profit_margin: {
                    ...rounds[roundIndex].profit_margin,
                    price_per_bike_dollars: price.toFixed(2),
                },
            });
            showToast(`Price updated for Round ${roundIndex + 1}.`, 'success');
            setEditingField(null);
            onUpdate();
        } catch (err) {
            showToast('Error saving price.', 'error');
        }
    };

    const handleSaveRnd = async (roundIndex) => {
        const upgrades = {};
        ATTRIBUTES.forEach(attr => {
            const v = parseFloat(rndInputs[attr]);
            upgrades[attr] = isNaN(v) || v < 0 ? 0 : v;
        });
        try {
            await updateTeamRoundData(teamName, 'Master', roundIndex, {
                rnd: { ...rounds[roundIndex].rnd, upgrades },
            });
            showToast(`R&D updated for Round ${roundIndex + 1}.`, 'success');
            setEditingField(null);
            onUpdate();
        } catch (err) {
            showToast('Error saving R&D.', 'error');
        }
    };

    const handleSaveAds = async (roundIndex) => {
        const performance_ads = {};
        const brand_ads = {};
        AD_KEYS.forEach(key => {
            const pv = parseFloat(perfAdsInputs[key]);
            const bv = parseFloat(brandAdsInputs[key]);
            performance_ads[key] = isNaN(pv) || pv < 0 ? 0 : pv;
            brand_ads[key] = isNaN(bv) || bv < 0 ? 0 : bv;
        });
        try {
            await updateTeamRoundData(teamName, 'Master', roundIndex, {
                advertising: {
                    ...rounds[roundIndex].advertising,
                    performance_ads,
                    brand_ads,
                },
            });
            showToast(`Advertising updated for Round ${roundIndex + 1}.`, 'success');
            setEditingField(null);
            onUpdate();
        } catch (err) {
            showToast('Error saving advertising.', 'error');
        }
    };

    const handleSaveSegments = async (roundIndex) => {
        try {
            await updateTeamRoundData(teamName, 'Master', roundIndex, {
                advertising: {
                    ...rounds[roundIndex].advertising,
                    segments: segmentInputs,
                },
            });
            showToast(`Segments updated for Round ${roundIndex + 1}.`, 'success');
            setEditingField(null);
            onUpdate();
        } catch (err) {
            showToast('Error saving segments.', 'error');
        }
    };

    const toggleSegment = (seg) => {
        setSegmentInputs(prev =>
            prev.includes(seg) ? prev.filter(s => s !== seg) : [...prev, seg]
        );
    };

    return (
        <div style={styles.container}>
            {rounds.map((round, i) => {
                if (!round?.is_submitted) return null;

                const bike = round.manufacturing?.upgraded_bike || round.manufacturing?.attributes || {};
                const price = parseFloat(round.profit_margin?.price_per_bike_dollars) || 0;
                const rnd = round.rnd?.upgrades || {};
                const perfAds = round.advertising?.performance_ads || {};
                const brandAds = round.advertising?.brand_ads || {};
                const segments = round.advertising?.segments || [];

                const totalPerf = Object.values(perfAds).reduce((s, v) => s + (parseFloat(v) || 0), 0);
                const totalBrand = Object.values(brandAds).reduce((s, v) => s + (parseFloat(v) || 0), 0);
                const totalRnd = Object.values(rnd).reduce((s, v) => s + (parseFloat(v) || 0), 0);

                const isEditingBike = editingField === `${i}-bike`;
                const isEditingPrice = editingField === `${i}-price`;
                const isEditingRnd = editingField === `${i}-rnd`;
                const isEditingAds = editingField === `${i}-ads`;
                const isEditingSegments = editingField === `${i}-segments`;

                return (
                    <div key={i} style={styles.roundSection}>
                        <div style={styles.roundTitle}>Round {i + 1}</div>

                        {/* Bike Attributes */}
                        <div style={styles.row}>
                            <span style={styles.label}>Bike:</span>
                            <div style={styles.value}>
                                {isEditingBike ? (
                                    <div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '6px' }}>
                                            {ATTRIBUTES.map(attr => (
                                                <label key={attr} style={{ fontSize: '11px', display: 'flex', alignItems: 'center', gap: '2px' }}>
                                                    {attr}:
                                                    <input
                                                        type="number"
                                                        min={1}
                                                        max={5}
                                                        value={attrInputs[attr] ?? 1}
                                                        onChange={(e) => setAttrInputs({ ...attrInputs, [attr]: e.target.value })}
                                                        style={styles.attrInput}
                                                    />
                                                </label>
                                            ))}
                                        </div>
                                        <button style={styles.saveBtn} onClick={() => handleSaveBike(i)}>Save</button>
                                        <button style={styles.cancelBtn} onClick={() => setEditingField(null)}>Cancel</button>
                                    </div>
                                ) : (
                                    <>
                                        {ATTRIBUTES.map(attr => `${attr}=${bike[attr] ?? '-'}`).join(', ')}
                                        <button style={styles.editBtn} onClick={() => startEditBike(i)}>Edit</button>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Price */}
                        <div style={styles.row}>
                            <span style={styles.label}>Price:</span>
                            <div style={styles.value}>
                                {isEditingPrice ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <span>$</span>
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            value={priceInput}
                                            onChange={(e) => setPriceInput(e.target.value)}
                                            onKeyDown={(e) => { if (e.key === 'Enter') handleSavePrice(i); if (e.key === 'Escape') setEditingField(null); }}
                                            autoFocus
                                            style={styles.priceInput}
                                        />
                                        <button style={styles.saveBtn} onClick={() => handleSavePrice(i)}>Save</button>
                                        <button style={styles.cancelBtn} onClick={() => setEditingField(null)}>Cancel</button>
                                    </div>
                                ) : (
                                    <>
                                        {fmtDollar(price)}
                                        <button style={styles.editBtn} onClick={() => startEditPrice(i)}>Edit</button>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* R&D */}
                        <div style={styles.row}>
                            <span style={styles.label}>R&D:</span>
                            <div style={styles.value}>
                                {isEditingRnd ? (
                                    <div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '6px' }}>
                                            {ATTRIBUTES.map(attr => (
                                                <label key={attr} style={{ fontSize: '11px', display: 'flex', alignItems: 'center', gap: '2px' }}>
                                                    {attr}: $
                                                    <input
                                                        type="number"
                                                        min={0}
                                                        step={1000}
                                                        value={rndInputs[attr] ?? 0}
                                                        onChange={(e) => setRndInputs({ ...rndInputs, [attr]: e.target.value })}
                                                        style={styles.dollarInput}
                                                    />
                                                </label>
                                            ))}
                                        </div>
                                        <button style={styles.saveBtn} onClick={() => handleSaveRnd(i)}>Save</button>
                                        <button style={styles.cancelBtn} onClick={() => setEditingField(null)}>Cancel</button>
                                    </div>
                                ) : (
                                    <>
                                        {totalRnd > 0
                                            ? ATTRIBUTES.filter(a => (parseFloat(rnd[a]) || 0) > 0)
                                                .map(a => `${a}=${fmtAdSpend(rnd[a])}`)
                                                .join(', ')
                                            : 'None'}
                                        <button style={styles.editBtn} onClick={() => startEditRnd(i)}>Edit</button>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Advertising */}
                        <div style={styles.row}>
                            <span style={styles.label}>Ads:</span>
                            <div style={styles.value}>
                                {isEditingAds ? (
                                    <div>
                                        <div style={styles.editLabel}>Performance Ads:</div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '6px' }}>
                                            {AD_KEYS.map(key => (
                                                <label key={key} style={{ fontSize: '11px', display: 'flex', alignItems: 'center', gap: '2px' }}>
                                                    {key}: $
                                                    <input
                                                        type="number"
                                                        min={0}
                                                        step={1000}
                                                        value={perfAdsInputs[key] ?? 0}
                                                        onChange={(e) => setPerfAdsInputs({ ...perfAdsInputs, [key]: e.target.value })}
                                                        style={styles.dollarInput}
                                                    />
                                                </label>
                                            ))}
                                        </div>
                                        <div style={styles.editLabel}>Brand Ads:</div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '6px' }}>
                                            {AD_KEYS.map(key => (
                                                <label key={key} style={{ fontSize: '11px', display: 'flex', alignItems: 'center', gap: '2px' }}>
                                                    {key}: $
                                                    <input
                                                        type="number"
                                                        min={0}
                                                        step={1000}
                                                        value={brandAdsInputs[key] ?? 0}
                                                        onChange={(e) => setBrandAdsInputs({ ...brandAdsInputs, [key]: e.target.value })}
                                                        style={styles.dollarInput}
                                                    />
                                                </label>
                                            ))}
                                        </div>
                                        <button style={styles.saveBtn} onClick={() => handleSaveAds(i)}>Save</button>
                                        <button style={styles.cancelBtn} onClick={() => setEditingField(null)}>Cancel</button>
                                    </div>
                                ) : (
                                    <>
                                        Perf: {fmtAdSpend(totalPerf)}, Brand: {fmtAdSpend(totalBrand)}
                                        <button style={styles.editBtn} onClick={() => startEditAds(i)}>Edit</button>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Segments */}
                        <div style={styles.row}>
                            <span style={styles.label}>Segments:</span>
                            <div style={styles.value}>
                                {isEditingSegments ? (
                                    <div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', marginBottom: '6px' }}>
                                            {SEGMENT_NAMES.map(seg => (
                                                <label key={seg} style={styles.checkboxLabel}>
                                                    <input
                                                        type="checkbox"
                                                        checked={segmentInputs.includes(seg)}
                                                        onChange={() => toggleSegment(seg)}
                                                    />
                                                    {seg}
                                                </label>
                                            ))}
                                        </div>
                                        <button style={styles.saveBtn} onClick={() => handleSaveSegments(i)}>Save</button>
                                        <button style={styles.cancelBtn} onClick={() => setEditingField(null)}>Cancel</button>
                                    </div>
                                ) : (
                                    <>
                                        {segments.length > 0
                                            ? segments.map(seg => (
                                                <span key={seg} style={styles.segBadge}>{seg}</span>
                                            ))
                                            : <span style={{ color: '#999' }}>None</span>}
                                        <button style={styles.editBtn} onClick={() => startEditSegments(i)}>Edit</button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                );
            })}

            {rounds.every(r => !r?.is_submitted) && (
                <p style={styles.noData}>No rounds submitted yet.</p>
            )}
        </div>
    );
};

export default TeamDataPanel;
